import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getAuth } from 'firebase/auth';
import { useAuth } from '@/lib/auth/AuthProvider';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FeatureMap = Record<string, boolean>;

interface FeatureContextType {
  features: FeatureMap;
  isLoading: boolean;
  useFeature: (flag: string) => boolean;
  useFeatures: () => FeatureMap;
}

// ---------------------------------------------------------------------------
// Cache helpers (sessionStorage, 5-minute TTL)
// ---------------------------------------------------------------------------

const CACHE_TTL_MS = 5 * 60 * 1000;
const cacheKey = (tenantId: string) => `feature_flags_${tenantId}`;

interface CacheEntry {
  features: FeatureMap;
  expiresAt: number;
}

function readCache(tenantId: string): FeatureMap | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(tenantId));
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() > entry.expiresAt) {
      sessionStorage.removeItem(cacheKey(tenantId));
      return null;
    }
    return entry.features;
  } catch {
    return null;
  }
}

function writeCache(tenantId: string, features: FeatureMap): void {
  try {
    const entry: CacheEntry = { features, expiresAt: Date.now() + CACHE_TTL_MS };
    sessionStorage.setItem(cacheKey(tenantId), JSON.stringify(entry));
  } catch {
    // sessionStorage may be unavailable (private browsing, quota exceeded)
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const FeatureContext = createContext<FeatureContextType | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const FeatureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [features, setFeatures] = useState<FeatureMap>({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchFeatures = useCallback(async (tenantId: string) => {
    // 1. Check cache first
    const cached = readCache(tenantId);
    if (cached) {
      setFeatures(cached);
      setIsLoading(false);
      return;
    }

    // 2. Fetch from API
    try {
      const auth = getAuth();
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        setIsLoading(false);
        return;
      }

      const token = await firebaseUser.getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl) {
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/features`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const featureMap: FeatureMap = data.features ?? {};
        writeCache(tenantId, featureMap);
        setFeatures(featureMap);
      }
      // On error: leave features as {} (deny-by-default)
    } catch {
      // Network error or parse failure — deny-by-default
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated || !user?.tenant_id) {
      setFeatures({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    fetchFeatures(user.tenant_id);
  }, [isAuthenticated, authLoading, user?.tenant_id, fetchFeatures]);

  const contextValue = useMemo(
    (): FeatureContextType => ({
      features,
      isLoading,
      useFeature: (flag: string) => features[flag] === true,
      useFeatures: () => features,
    }),
    [features, isLoading]
  );

  return <FeatureContext.Provider value={contextValue}>{children}</FeatureContext.Provider>;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useFeatureContext(): FeatureContextType {
  const ctx = useContext(FeatureContext);
  if (!ctx) {
    throw new Error('useFeature / useFeatures must be used within a FeatureProvider');
  }
  return ctx;
}

/** Returns true if the named feature flag is enabled for the current tenant. */
export function useFeature(flag: string): boolean {
  const { features } = useFeatureContext();
  return features[flag] === true;
}

/** Returns the full feature map for the current tenant. */
export function useFeatures(): FeatureMap {
  const { features } = useFeatureContext();
  return features;
}

/** Returns true while features are being fetched (useful for avoiding flash). */
export function useFeaturesLoading(): boolean {
  const { isLoading } = useFeatureContext();
  return isLoading;
}
